// Help Center content block types, inline tokens, and route param keys.

import { BANNER_DEFAULT_APPEARANCE } from 'src/services/consts/banner/appearance.const'
import { REGULATION_CARDS } from 'src/services/consts/banner/behaviour.const'
import {
  COMPLIANCE_HEALTH_LEVEL_DISPLAY,
  COMPLIANCE_HEALTH_LEVELS,
} from 'src/services/consts/banner/detail.const'
import {
  BANNER_INSTALL_STATUS,
  BANNER_INSTALL_STATUS_CHIP_MAP,
} from 'src/services/consts/banner/install.const'

export const FAQ_BLOCK_TYPES = {
  PARAGRAPH: 'paragraph',
  SECTION: 'section',
  STEPS: 'steps',
  BULLETS: 'bullets',
  NOTE: 'note',
  CODE: 'code',
  CODE_TABS: 'code-tabs',
  TABLE: 'table',
  IMAGE: 'image',
  PREVIEW: 'preview',
  VIDEO: 'video',
}

export const FAQ_SECTION_ICON_TONES = {
  BRAND: 'brand',
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
  WARNING: 'warning',
  INFO: 'info',
  CYAN: 'cyan',
  VIOLET: 'violet',
}

export const FAQ_SECTION_ICON_TONE_MAP = {
  [FAQ_SECTION_ICON_TONES.BRAND]: { color: 'brand-dark', fill: 'brand-extra-light' },
  [FAQ_SECTION_ICON_TONES.POSITIVE]: { color: 'positive', fill: 'positive-light' },
  [FAQ_SECTION_ICON_TONES.NEGATIVE]: { color: 'negative', fill: 'negative-light' },
  [FAQ_SECTION_ICON_TONES.WARNING]: { color: 'cta-dark', fill: 'cta-light' },
  [FAQ_SECTION_ICON_TONES.INFO]: { color: 'secondary', fill: 'accent-light' },
  [FAQ_SECTION_ICON_TONES.CYAN]: { color: 'cyan', fill: 'cyan-light' },
  [FAQ_SECTION_ICON_TONES.VIOLET]: { color: 'violet-muted', fill: 'violet-light' },
}

/** Placeholder CDN URL for the FAQ `code-tabs` install variant (no real siteId). */
export const FAQ_INSTALL_DEFAULT_SCRIPT_URL =
  'https://cdn.cookiearmor.com/YOUR_SITE_ID/bootstrap.js'

/** Closed `preview` block variants. Article JSON names `variant` only. */
export const FAQ_PREVIEW_VARIANTS = {
  EVENT_STATS: 'event-stats',
  APPEARANCE: 'appearance',
}

/** Fixture for the `event-stats` preview (not live site data). */
export const FAQ_EVENT_STATS_PREVIEW_SUMMARY = {
  totalEvents: 120,
  byClassification: { acceptAll: 70, rejectAll: 30, custom: 20 },
}

/** Fixture for the `appearance` preview (product defaults, not a live draft). */
export const FAQ_APPEARANCE_PREVIEW = {
  type: BANNER_DEFAULT_APPEARANCE.type,
  theme: BANNER_DEFAULT_APPEARANCE.theme,
  position: BANNER_DEFAULT_APPEARANCE.position,
  accentColor: BANNER_DEFAULT_APPEARANCE.accentColor,
}

// ── Bullet display variants ────────────────────────────────────────────────
// default   - standard <ul> list (no variant field needed)
// grid      - 2-column icon-card grid (items may have icon, title)
// checklist - inset card with per-item icon and optional eyebrow title; icon is tone-driven (POSITIVE = green check, NEGATIVE = red X). Defaults to POSITIVE.
// journey   - vertical icon-row list for cycle/overview descriptions (icon + title + text)
export const BULLET_VARIANTS = {
  GRID: 'grid',
  CHECKLIST: 'checklist',
  JOURNEY: 'journey',
}

/** Checklist bullet icon tones. Defaults to POSITIVE for backward compatibility. */
export const CHECKLIST_TONES = {
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
}

export const CHECKLIST_TONE_MAP = {
  [CHECKLIST_TONES.POSITIVE]: { icon: 'check-circle', color: 'positive' },
  [CHECKLIST_TONES.NEGATIVE]: { icon: 'circle-x', color: 'negative' },
}

/** Route query param key for the search term (used when navigating from search to topic/article for highlight). */
export const FAQ_SEARCH_QUERY_PARAM = 'q'

export const FAQ_ROUTE_PARAMS = {
  TOPIC_ID: 'topicId',
  ARTICLE_ID: 'articleId',
}

/** Fixed section IDs used by the article TOC, scroll spy, and search-to-section. */
export const FAQ_ARTICLE_SECTION_IDS = {
  OVERVIEW: 'overview',
  COMMON_QUESTIONS: 'common-questions',
}

export const FAQ_BTN_VARIANTS = {
  FILLED: 'filled',
  OUTLINE: 'outline',
  DESTRUCTIVE: 'destructive',
  GHOST: 'ghost',
}

/** Chip visual style per variant. Mirrors the shipped q-btn appearance. */
export const FAQ_BTN_VARIANT_CLASS_MAP = {
  [FAQ_BTN_VARIANTS.FILLED]: 'bg-brand-dark text-surface',
  [FAQ_BTN_VARIANTS.OUTLINE]: 'text-info border-info',
  [FAQ_BTN_VARIANTS.DESTRUCTIVE]: 'bg-negative text-surface',
  [FAQ_BTN_VARIANTS.GHOST]: 'text-secondary',
}

export const FAQ_BTN_DEFAULT_CONFIG = {
  variant: FAQ_BTN_VARIANTS.FILLED,
  icon: null,
}

/** Button id → display spec for inline `{btn:id}` tokens. Labels match shipped UI exactly. */
export const FAQ_BUTTONS = {
  'publish-changes': {
    label: 'Publish changes',
    variant: FAQ_BTN_VARIANTS.FILLED,
    icon: 'send',
  },
  discard: {
    label: 'Discard',
    variant: FAQ_BTN_VARIANTS.OUTLINE,
    icon: 'refresh-cw',
  },
  'scan-now': {
    label: 'Scan now',
    variant: FAQ_BTN_VARIANTS.FILLED,
    icon: 'scan-search',
  },
  'run-scan': {
    label: 'Run scan',
    variant: FAQ_BTN_VARIANTS.GHOST,
    icon: 'scan-search',
  },
  'save-changes': {
    label: 'Save Changes',
    variant: FAQ_BTN_VARIANTS.FILLED,
    icon: null,
  },
  'save-and-scan': {
    label: 'Save & scan',
    variant: FAQ_BTN_VARIANTS.FILLED,
    icon: null,
  },
  'go-back': {
    label: 'Go back',
    variant: FAQ_BTN_VARIANTS.OUTLINE,
    icon: null,
  },
  cancel: {
    label: 'Cancel',
    variant: FAQ_BTN_VARIANTS.OUTLINE,
    icon: null,
  },
  'delete-banner': {
    label: 'Delete banner',
    variant: FAQ_BTN_VARIANTS.DESTRUCTIVE,
    icon: null,
  },
  delete: {
    label: 'Delete',
    variant: FAQ_BTN_VARIANTS.DESTRUCTIVE,
    icon: null,
  },
  reset: {
    label: 'Reset',
    variant: FAQ_BTN_VARIANTS.GHOST,
    icon: 'refresh-cw',
  },
  'look-up-consent': {
    label: 'Look up consent',
    variant: FAQ_BTN_VARIANTS.FILLED,
    icon: null,
  },
  'copy-consent-record': {
    label: 'Copy consent record',
    variant: FAQ_BTN_VARIANTS.GHOST,
    icon: 'copy',
  },
}

export const FAQ_STATUS_DEFAULT_CONFIG = {
  label: '',
  textClass: 'text-info',
  bgClass: 'bg-surface-dark',
}

const HEALTHY_DISPLAY = COMPLIANCE_HEALTH_LEVEL_DISPLAY[COMPLIANCE_HEALTH_LEVELS.GOOD]
const NEEDS_ATTENTION_DISPLAY = COMPLIANCE_HEALTH_LEVEL_DISPLAY[COMPLIANCE_HEALTH_LEVELS.MODERATE]
const NEEDS_WORK_DISPLAY = COMPLIANCE_HEALTH_LEVEL_DISPLAY[COMPLIANCE_HEALTH_LEVELS.POOR]

function faqStatusFromChip({ label, chipClass }) {
  const classes = chipClass.split(/\s+/)
  return {
    label,
    textClass: classes.find((className) => className.startsWith('text-')),
    bgClass: classes.find((className) => className.startsWith('bg-')),
  }
}

/** Status id → display spec for inline `{status:id}` tokens. Labels/colors match Overview. */
export const FAQ_STATUSES = {
  healthy: {
    label: HEALTHY_DISPLAY.label,
    textClass: HEALTHY_DISPLAY.textClass,
    bgClass: HEALTHY_DISPLAY.bgClass,
  },
  'needs-attention': {
    label: NEEDS_ATTENTION_DISPLAY.label,
    textClass: NEEDS_ATTENTION_DISPLAY.textClass,
    bgClass: NEEDS_ATTENTION_DISPLAY.bgClass,
  },
  'needs-work': {
    label: NEEDS_WORK_DISPLAY.label,
    textClass: NEEDS_WORK_DISPLAY.textClass,
    bgClass: NEEDS_WORK_DISPLAY.bgClass,
  },
  'script-installed': faqStatusFromChip(
    BANNER_INSTALL_STATUS_CHIP_MAP[BANNER_INSTALL_STATUS.INSTALLED]
  ),
  'script-missing': faqStatusFromChip(
    BANNER_INSTALL_STATUS_CHIP_MAP[BANNER_INSTALL_STATUS.MISSING]
  ),
  'couldnt-check': faqStatusFromChip(BANNER_INSTALL_STATUS_CHIP_MAP[BANNER_INSTALL_STATUS.ERROR]),
  'access-blocked': faqStatusFromChip(
    BANNER_INSTALL_STATUS_CHIP_MAP[BANNER_INSTALL_STATUS.BLOCKED]
  ),
}

/** Regulation value → picker card. `{regulation:id}` ids are `BANNER_REGULATION` values. */
export const FAQ_REGULATION_CARDS = Object.fromEntries(
  REGULATION_CARDS.map((card) => [card.value, card])
)

/** Bold, links, mock-button chips, status chips, regulation badges, and inline code. */
export const INLINE_TOKEN_PATTERN =
  /(\*\*[^*]+\*\*|\[\[[^\]]+\]\]|\{btn:[^}]+\}|\{status:[^}]+\}|\{regulation:[^}]+\}|`[^`\n]+`)/g

export const INLINE_TOKEN_TYPES = {
  TEXT: 'text',
  BOLD: 'bold',
  LINK: 'link',
  EXTERNAL_LINK: 'external-link',
  BTN: 'btn',
  STATUS: 'status',
  REGULATION: 'regulation',
  BREAK: 'break',
  CODE: 'code',
}
